<template>
  <SfListItem
    :key="segment.id"
    :item-id="itemId"
    :cornered="true"
    :class="segmentClass"
    variant="lg"
    class="sf-pad-h-1 tw-group"
    @item-click="itemClick"
  >
    <template #default>
      <div
        v-if="isHighlighted && loading"
        class="sf-spinner-wrapper sf-pad-r-1 sf-inline-block"
      >
        <div class="sf-spinner sf-spinner-primary"></div>
      </div>
      {{ segment.name }}
      <slot name="tooltip"> </slot>
    </template>

    <template #right-content>
      <div
        v-if="!isFakeSegment && !isPartnerEditMode"
        class="tw-w-11 tw-text-right tw-invisible group-hover:tw-visible"
        :class="iconsClass"
        @click.stop.prevent="openEditModal"
      >
        <SegmentIcon :segment="segment" />

        <i
          v-if="segment.permissions?.update"
          v-tooltip="editIconTooltip"
          class="
            sf-icon
            fa fa-pencil
            sf-c-text-light
            tw-pl-2
            hover:tw-opacity-70
          "
          data-testid="edit-segment-icon"
        />
      </div>
    </template>
  </SfListItem>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSegmentsStore } from '@app/insights/store/segments'
import type { Segment } from '../../types/index'
import SfListItem from '@app/shared/vue/components/sf-list/sf-list-item.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import SegmentIcon from './segment-icon.vue'

const I18n = useI18n()
const route = useRoute()

const editIconTooltip = I18n.t('companies_overview.segments_new.edit_query')

interface Props {
  segment: Segment
  loading: boolean
}

const props = defineProps<Props>()
const emit =
  defineEmits<{
    'item-click': [value: Segment]
  }>()

const currentSegment = computed(() => useSegmentsStore().segment as Segment)

const isCurrentSegment = computed(
  () => currentSegment.value?.id === props.segment.id
)
const isFakeSegment = computed(() => props.segment.id === 0)
const noSegmentSelected = computed(
  () => !currentSegment.value && isFakeSegment.value
)

const isHighlighted = computed(
  () => isCurrentSegment.value || noSegmentSelected.value
)

const segmentClass = computed(() => ({
  'sf-bg-primary-subtle': isHighlighted.value,
}))

const iconsClass = computed(() => ({
  '!tw-visible': isCurrentSegment.value,
}))

const itemId = computed(() => props.segment.id.toString())

const isPartnerEditMode = computed(() => !!route.query.ms_id)

const itemClick = (): void => {
  emit('item-click', props.segment)
}

const segmentsStore = useSegmentsStore()

const openEditModal = (): void => {
  segmentsStore.openEditSegmentModal(props.segment)
}
</script>
